import { get015Authority } from '@/apis/api/say015Apis';
import { calScrollEvent } from '@/apis/hooks/useScrollInfinity';
import SkeletonBox from '@/components/common/SkeletonBox';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { MdPhoneMissed } from 'react-icons/md';
import { PiPhoneIncomingFill, PiPhoneOutgoingFill, PiPhoneSlashFill } from 'react-icons/pi';
import { v4 as uuidv4 } from 'uuid';
import { getCallsHistory } from './sipApi/sipApi';
import * as S from './sipstyles/SipTable.style';
import { useLoaderTimer } from '@/shared/hooks/useLoaderTimer';
import { formatToUTCDateTime } from '@/shared/util/format/dateFormatUtil';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { callStatusRecoil, phoneNumberRecoil } from './sip-recoil/sipRecoil';
import { useModalHook } from '@/components/commons/modals/useModalHook';

const SipTable = () => {
  const [number015, setNumber015] = useState<string>('');
  const setPhoneNumber = useSetRecoilState(phoneNumberRecoil);
  const callStatus = useRecoilValue(callStatusRecoil);

  const { warningModal } = useModalHook();
  // 015구독 상태 알려주는 useQuery
  const {
    data: authorityData,
    isLoading: authorityDataIsLoading,
    refetch: authorityDataRefetch,
    isError: authorityIsError,
  } = useQuery(['authorityData'], get015Authority);

  // 전화 내역 불러오는 useQuery
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    ['/v1/sip/calls'],
    async ({ pageParam = 0 }) => getCallsHistory({ page: pageParam, size: 14 }),
    {
      getNextPageParam: (lastPage: any) => {
        const nextPage = lastPage.pageable.pageNumber + 1;
        return nextPage < lastPage.totalPages ? nextPage : undefined;
      },
      onSuccess: () => {},
      onError: (error) => {
        console.error('error', error);
      },
    },
  );

  const handleScroll = (event: any) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (
      calScrollEvent({ scrollHeight, clientHeight, scrollTop: scrollTop + 40 }) &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  // 응답 리스트 표출할 변수
  const historyData = data ? data.pages.flatMap((page) => page.content) : [];

  useEffect(() => {
    if (!authorityDataIsLoading && authorityData.number015) {
      setNumber015(authorityData.number015);
    }
  }, [data, authorityData]);

  /* 로딩 컴포넌트 표출 시간제어 hook */
  const timerHooks = useLoaderTimer({ isLoading: authorityDataIsLoading || isLoading });

  const onClickHistoryItem = (item: any) => {
    if (callStatus !== 'idle') warningModal('통화중', '현재 통화중입니다.');
    if (callStatus)
      if (number015) {
        if (item.sender === number015) {
          setPhoneNumber(item.receiver);
        } else {
          setPhoneNumber(item.sender);
        }
      } else {
        setPhoneNumber(number015);
      }
  };

  return (
    <S.Container>
      <S.TableWrap>
        <S.Table>
          {/* 테이블 헤더 */}
          <S.TableHeader>
            <S.TableCell>
              <S.CellWrap>통화상태</S.CellWrap>
            </S.TableCell>
            <S.TableCell>연락처</S.TableCell>
            <S.TableCell>통화시간</S.TableCell>
            <S.TableCell>통화날짜</S.TableCell>
          </S.TableHeader>

          {/* 테이블 바디 */}
          {!number015 && timerHooks.timer ? (
            <S.TableBody>
              {[...Array(14)].map((_) => (
                <S.TableCell key={uuidv4()} style={{ height: '5rem' }}>
                  <SkeletonBox
                    type="1"
                    title={false}
                    active
                    block
                    paragraph={{
                      rows: 1,
                      width: ['100%'],
                    }}
                  />
                </S.TableCell>
              ))}
            </S.TableBody>
          ) : (
            <S.TableBody onScroll={handleScroll}>
              {number015 &&
                historyData?.map((item) => {
                  return (
                    <S.TableRow
                      key={item.sequence}
                      onClick={() => {
                        onClickHistoryItem(item);
                      }}
                    >
                      <S.TableCell>
                        {number015 && item.disposition === 'ANSWERED' && item.sender === number015 && (
                          <S.IconTextWrap>
                            <PiPhoneOutgoingFill color="#666666" />
                            <p>발신</p>
                          </S.IconTextWrap>
                        )}
                        {number015 && item.disposition === 'ANSWERED' && item.receiver === number015 && (
                          <S.IconTextWrap>
                            <PiPhoneIncomingFill color="#00CF7C" />
                            <p>수신</p>
                          </S.IconTextWrap>
                        )}
                        {number015 && item.disposition === 'FAILED' && (
                          <S.IconTextWrap>
                            <MdPhoneMissed color="#FF5E48" />
                            <p>부재중</p>
                          </S.IconTextWrap>
                        )}
                        {number015 && item.disposition === 'BUSY' && (
                          <S.IconTextWrap>
                            <PiPhoneSlashFill color="#FF5E48" />
                            <p>실패</p>
                          </S.IconTextWrap>
                        )}
                        {/* NO ANSWER */}
                        {/* BUSY */}
                        {/* FAILED */}
                      </S.TableCell>
                      <S.TableCell>{number015 && item.sender === number015 ? item.receiver : item.sender}</S.TableCell>
                      <S.TableCell>{item.callSec}초</S.TableCell>
                      <S.TableCell>{formatToUTCDateTime(item.callDate)}</S.TableCell>
                    </S.TableRow>
                  );
                })}
            </S.TableBody>
          )}
        </S.Table>
      </S.TableWrap>
    </S.Container>
  );
};
export default SipTable;
